import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { type JSX, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  type Resolver,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { boolean, object, string } from 'yup'
import type { Device } from '@/pages/Device/Device'
import type { Composition } from '../composition'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import VerifyStepBase from './VerifyStepBase'
import {
  formLegendSx,
  requiredCheckboxGroupItemSx,
  requiredCheckboxLabelTextSx,
  requiredCheckboxSx,
} from '@/utils/theme'
import useFinishVerification from '@/hooks/useFinishVerfication'
import { showFieldError } from '@/components/SharedPreFlightFields'

interface Props {
  device: Device
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  setActiveStep: (step: number) => void
  finishSelfTest: (message: Record<string, unknown>) => void
  lastJsonMessage: Record<string, unknown> | null
}

type FormValues = {
  attachedToTrack: boolean
  flagPlacement: boolean
  uiPanelFunctions: boolean
  magnetsSecured: boolean
  remarks: string
}

const CHECKBOX_FIELDS: (keyof Omit<FormValues, 'remarks'>)[] = [
  'attachedToTrack',
  'flagPlacement',
  'uiPanelFunctions',
  'magnetsSecured',
]

const RdiVerifyStep = ({
  device,
  composition,
  setActiveStep,
  finishSelfTest,
  lastJsonMessage,
}: Props): JSX.Element => {
  const { t } = useTranslation(['app', 'common'])
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const schema = object({
    attachedToTrack: boolean().equals([true], '').required(),
    flagPlacement: boolean().equals([true], '').required(),
    uiPanelFunctions: boolean().equals([true], '').required(),
    magnetsSecured: boolean().equals([true], '').required(),
    remarks: string().optional(),
  })

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      attachedToTrack: false,
      flagPlacement: false,
      uiPanelFunctions: false,
      magnetsSecured: false,
      remarks: '',
    },
    mode: 'all',
  })

  const { isSaving, confirm } = useFinishVerification({
    device,
    composition,
    finishSelfTest,
    lastJsonMessage,
    setActiveStep,
  })

  const { serial, label, type } = device

  return (
    <FormProvider {...methods}>
      <VerifyStepBase
        serial={serial ?? ''}
        label={label}
        type={type}
        isSaving={isSaving}
        composition={composition}
        onBack={() => setActiveStep(2)}
        onFormSubmit={(e) => {
          setHasAttemptedSubmit(true)
          void methods.handleSubmit(() => {
            setShowConfirmDialog(true)
          })(e)
        }}
      >
        <FormGroup>
          <FormLabel component="legend" sx={formLegendSx}>
            {t('app:device.calibration.verifyStep.rdi.tested')}
          </FormLabel>
          {CHECKBOX_FIELDS.map((name) => (
            <Controller
              key={name}
              name={name}
              control={methods.control}
              render={({ field, fieldState }) => {
                const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
                return (
                  <FormControl
                    error={hasError}
                    sx={requiredCheckboxGroupItemSx}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={requiredCheckboxSx(hasError)}
                        />
                      }
                      label={
                        <>
                          <Box
                            component="span"
                            sx={requiredCheckboxLabelTextSx}
                          >
                            {t(`app:device.calibration.verifyStep.rdi.${name}`)}
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Box>
                        </>
                      }
                    />
                    {hasAttemptedSubmit && showFieldError(fieldState) && (
                      <FormHelperText sx={{ ml: 4 }}>
                        {showFieldError(fieldState)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )
              }}
            />
          ))}
        </FormGroup>

        <Controller
          name="remarks"
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('app:device.calibration.verifyStep.remarks')}
              fullWidth
              variant="standard"
              multiline
              minRows={1}
              maxRows={6}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mt: 2 }}
            />
          )}
        />

        <ConfirmDialog
          open={showConfirmDialog}
          setOpen={setShowConfirmDialog}
          onConfirm={() => {
            const remarks = methods.getValues('remarks')
            confirm({ remark: remarks, deviceType: device.type })
            setShowConfirmDialog(false)
          }}
          onCancel={() => setShowConfirmDialog(false)}
          onClose={() => setShowConfirmDialog(false)}
        >
          {t('app:device.calibration.verifyStep.confirm')}
        </ConfirmDialog>
      </VerifyStepBase>
    </FormProvider>
  )
}

export default RdiVerifyStep
